import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import BlogPost from "@/pages/BlogPost";
import { blogArticles, blogRedirects } from "@/data/blogArticles";
import { pageHead } from "@/lib/seo";

// Original publication dates of the six legacy French articles (the visible updated date stays in 2026).
const originalPublicationDates: Record<string, string> = {
  "levee-de-fonds-role-cmo": "2024-11-25",
  "recrutement-equipe-marketing-conseils": "2024-11-15",
  "intelligence-artificielle-marketing-guide": "2024-11-10",
  "generation-leads-b2b-strategies": "2024-11-05",
  "marketing-automation-guide-pme": "2024-11-01",
  "seo-local-bordeaux-guide": "2024-10-25",
};

export const Route = createFileRoute("/blog/$slug")({
  beforeLoad: ({ params }) => {
    const target = blogRedirects[params.slug];
    if (target) throw redirect({ href: target, statusCode: 301 });
    // Only French articles are published; untranslated English slugs are real 404s.
    if (!blogArticles.fr.some((a) => a.slug === params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const article = blogArticles.fr.find((a) => a.slug === params.slug);
    if (!article) {
      return pageHead({
        path: `/blog/${params.slug}`,
        title: "Page introuvable | CMO Partner",
        description: "Cette page n'existe pas.",
        enPath: null,
        noindex: true,
      });
    }
    const base = pageHead({
      path: `/blog/${article.slug}`,
      title: article.metaTitle,
      description: article.metaDescription,
      enPath: null,
    });
    const url = `https://www.cmopartner.fr/blog/${article.slug}`;
    return {
      ...base,
      meta: [
        ...(base.meta ?? []),
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            author: {
              "@type": "Person",
              name: "Tiphany Rigaudias",
              jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"],
              knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"],
              url: "https://www.cmopartner.fr/a-propos",
              sameAs: ["https://linkedin.com/in/tiphanyrigaudias"],
            },
            datePublished: originalPublicationDates[article.slug] ?? article.dateISO,
            dateModified: "2026-09-28",
            publisher: { "@type": "Organization", name: "CMO Partner", url: "https://www.cmopartner.fr" },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.cmopartner.fr/blog" },
              { "@type": "ListItem", position: 3, name: article.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPost,
});
